import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserDto } from '@nusign/picflow-backend-types';
import { filter, take } from 'rxjs';
import type Stripe from 'stripe';
import { CANNY } from '../canny';
import { trialValueMultiplier } from '../consts';
import { getFullPath } from '../lib/get-full-path';
import { getTenantGroups } from '../lib/get-tenant-groups';
import { isChecklistDone } from '../lib/is-checklist-done';
import { isIframe } from '../lib/is-iframe';
import { throttle } from '../lib/throttle';
import { PICFLOW_ANALYTICS } from '../picflow-analytics';
import { PICFLOW_CONFIG } from '../picflow-config';
import { Product } from '../types/product.type';
import { Promotion } from '../types/promotion.type';
import { BrowserService } from './browser.service';
import { SentryService } from './sentry.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { SubscriptionService } from './subscription.service';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analytics: any;
    Intercom: (action: string, ...args: unknown[]) => void;
    intercomSettings: { [key: string]: unknown };
  }
}

@Injectable({
  providedIn: 'root',
})
export class LoggedInAnalyticsService {
  private analytics = inject(PICFLOW_ANALYTICS);
  private state = inject(StateService);
  private platformId = inject(PLATFORM_ID);
  private config = inject(PICFLOW_CONFIG);
  private browserService = inject(BrowserService);
  private subscriptionService = inject(SubscriptionService);
  private storage = inject(StorageService);
  private sentryService = inject(SentryService);
  private route = inject(ActivatedRoute);
  private canny = inject(CANNY, { optional: true });

  sendLoginEvent = false;
  cannyIdentified = false;

  private tenantWatcher = this.state.activeTenant$.subscribe(tenant => {
    this.analytics.setTenant(tenant);
  });

  identify(user?: UserDto) {
    if (this.state.impersonating) return;
    if (!user) return;
    this.sentryService.setUser({ id: user.id, clerk_id: user.sub });

    const tenant = this.analytics.tenant;

    if (!isPlatformBrowser(this.platformId)) return;
    window.intercomSettings = {
      app_id: this.config.intercomAppId,
      hide_default_launcher: true,
      user_id: user.id,
      user_hash: user.intercom_hash,
      email: user.email,
      featureFlags: getTenantGroups(tenant),
    };

    if (tenant) {
      window.intercomSettings.checklist = this.getCheckListStatus();
      window.intercomSettings.instagram = tenant.socials.instagram;
    }

    const values = this.getUtmItentify(user);
    if (values.first_utm) window.intercomSettings.first_utm = values.first_utm;
    if (values.last_utm) window.intercomSettings.last_utm = values.last_utm;
    if (values.first_referrer) window.intercomSettings.first_referrer = values.first_referrer;

    window.Intercom?.('update');
    this.analytics.intercomInit();

    this.throttledIdentify(user);

    if (this.sendLoginEvent) this.analytics.track('login'); // track login event after setting data layer variables
    this.sendLoginEvent = false;

    this.analytics.userTraits = this.getUserTraits();
  }

  private throttledIdentify = throttle(user => {
    if (this.browserService.isExtension()) return;
    this.cannyIdentify(user);
    try {
      window.analytics?.identify(user.id, this.getUserTraits());
    } catch (err) {
      console.warn('Unable to call segment identify');
      console.error(err);
    }
  }, 2000);

  private cannyIdentify(user: UserDto) {
    if (this.cannyIdentified) return;
    if (isIframe()) return;
    this.cannyIdentified = true;
    if (!this.canny) return;

    this.canny.get().subscribe(canny => {
      this.subscriptionService
        .getMainPrice()
        .pipe(filter(Boolean), take(1))
        .subscribe(mainPrice => {
          let amount = 0;
          if (mainPrice) {
            amount = mainPrice.unit_amount / 100;
            if (mainPrice.recurring.interval === 'year') amount /= 12;
          }

          // Use the Canny SDK to identify the current user of your website
          canny('identify', {
            appID: '611b5553d6c0026f4725118a',
            user: {
              email: user.email,
              name: user.first_name + ' ' + user.last_name,
              id: user.id,
              companies: this.analytics.tenant
                ? [
                    {
                      created: new Date(user.created_at * 1000).toISOString(),
                      monthlySpend: amount,
                      name: this.analytics.tenant.name,
                      id: this.analytics.tenant.id,
                    },
                  ]
                : [],
              avatarURL: user.avatar_url,
              created: new Date(user.created_at * 1000).toISOString(),
            },
          });
        });
    });
  }

  startTrialEvent(product: Product, price: Stripe.Price, promo: Promotion, total: number) {
    this.analytics.track('trial_started', true, {
      order_id: this.analytics.tenant.id + '_trial_started',
      ...this.getEcommerceProperties(product, price, promo, total, trialValueMultiplier),
    });
  }

  cancelTrialEvent(analyticsData: { [key: string]: string | number }) {
    this.analytics.track('cancel_trial', true, {
      order_id: this.analytics.tenant.id + '_trial_started', // should be trial_started because it will be used to cancel the original conversion
      ...analyticsData,
    });
  }

  cancelSubscriptionEvent(analyticsData: { [key: string]: string | number }) {
    this.analytics.track('cancel_subscription', true, {
      order_id: this.analytics.tenant.id + '_converted',
      ...analyticsData,
    });
  }

  public changePlanEvent(product: Product, price: Stripe.Price, promo: Promotion, total: number) {
    this.analytics.track('change_plan_success', true, {
      ...this.getEcommerceProperties(product, price, promo, total),
    });
  }

  private getEcommerceProperties(
    product: Product,
    price: Stripe.Price,
    promo: Promotion,
    total: number,
    multiplier = 1
  ) {
    let value = total;
    if (price.recurring.interval === 'month')
      value *= 9; // assuming 9 months average lifetime
    else if (price.recurring.interval === 'year') value *= 1.5; // assuming 1.5 years average lifetime

    return {
      currency: 'USD',
      total: value,
      value: value,
      predicted_ltv: value * multiplier,
      coupon: promo?.code,
      products: [
        {
          product_id: product.id,
          name: product.name,
          price: value * multiplier,
        },
      ],
    };
  }

  private getCheckListStatus() {
    const map = {
      uploadAssets: 'Upload',
      setCover: 'Cover',
      uploadLogo: 'Logo',
      firstFeedback: 'Review',
      deliverAssets: 'Deliver',
      visitDash: 'Dash',
    };
    const checkList = this.analytics.tenant.onboarding_checklist;
    const done = isChecklistDone(this.analytics.tenant);
    if (done) return '✅';
    if (!checkList) return '❌';
    return (
      Object.keys(checkList)
        .map(key => `${checkList[key] ? '✅' : '❌'} ${map[key]}`)
        .join(' ') || '❌'
    );
  }
  private getUserType() {
    if (this.state.user) return 'customer';
    const path = getFullPath(this.route.snapshot);
    if (path[0] === 'gallery' || this.config.isTenantDomain) return 'gallery_guest';
    return 'potential_customer';
  }

  private getUserTraits() {
    const user = this.state.user;
    const utm = this.getUtmItentify(user);
    const trackingParams = this.analytics.getTrackingParams();

    const params = {
      utm_first_source: utm.first_utm ? utm.source : undefined,
      utm_first_medium: utm.first_utm ? utm.medium : undefined,
      utm_first_campaign: utm.first_utm ? utm.campaign : undefined,
      utm_first_term: utm.first_utm ? utm.term : undefined,
      utm_first_content: utm.first_utm ? utm.content : undefined,
      first_referrer: utm.first_referrer ?? undefined,
      first_path: utm.first_path ?? undefined,
      userType: this.getUserType(),
      ...trackingParams,
    };

    if (!user) return params;
    return {
      ...params,
      userId: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
    };
  }

  private getUtmItentify(user?: UserDto) {
    const values: { [key: string]: string | number } = {};
    const firstUtm: { key: string; value: string | number }[] = this.storage.get('firstUtm') || [];
    if (firstUtm.length) {
      firstUtm.forEach(data => (values[data.key] = data.value));

      const firstUtmTime = <number>firstUtm.find(t => t.key === 'time').value;

      // Only report first utm when the utm time was before the user created date
      if (!user || firstUtmTime < user.created_at * 1000) {
        firstUtm.forEach(s => s.key === 'time' && (s.value = new Date(s.value).toUTCString()));
        values.first_utm = firstUtm.map(s => `${s.key} = ${s.value}`).join(' | ');
      }
    }
    const firstReferrer = this.storage.get<{ time: number; referrer: string }>('firstReferrer');
    if (firstReferrer) {
      if (!user || firstReferrer.time < user.created_at * 1000) {
        values.first_referrer = firstReferrer.referrer;
      }
    }

    const lastUtm: { key: string; value: string | number }[] = this.storage.get('lastUtm') || [];
    if (lastUtm.length) {
      const lastUtmTime = <number>lastUtm.find(t => t.key === 'time').value;

      // Only report last utm when the utm time was before the user created date
      if (!user || lastUtmTime < user.created_at * 1000) {
        lastUtm.forEach(s => s.key === 'time' && (s.value = new Date(s.value).toUTCString()));
        values.last_utm = lastUtm.map(s => `${s.key} = ${s.value}`).join(' | ');
      }
    }
    return values;
  }
}
